import { getApiUrl } from '../utils/apiConfig';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { getAuthHeaders } from '../utils/userUtils';

const request = async (currentUser, path, options = {}) => {
  if (!currentUser) {
    throw new Error('로그인이 필요해요.');
  }
  const headers = await getAuthHeaders(currentUser);
  const method = String(options.method || 'GET').toUpperCase();
  let response;
  try {
    response = await fetchWithRetry(getApiUrl(path), {
      ...options,
      headers: {
        ...headers,
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
    }, {
      maxRetries: method === 'DELETE' ? 0 : 1,
      timeoutMs: 15_000,
      retryOnTimeout: method !== 'DELETE',
    });
  } catch (requestError) {
    const message = /[가-힣]/.test(requestError?.message || '')
      ? requestError.message
      : '네트워크 연결을 확인한 뒤 다시 시도해주세요.';
    const safeError = new Error(message);
    safeError.status = 0;
    throw safeError;
  }

  let payload = {};
  try {
    payload = await response.json();
  } catch (_error) {
    payload = {};
  }
  if (!response.ok) {
    const safeServerDetail = (
      typeof payload.detail === 'string'
      && payload.detail.length <= 200
      && /[가-힣]/.test(payload.detail)
    ) ? payload.detail : null;
    const messages = {
      401: '로그인 정보를 다시 확인해주세요.',
      403: '이 설정을 변경할 권한이 없어요.',
      404: safeServerDetail || 'AI 맞춤 설정을 준비하고 있어요.',
      409: safeServerDetail || '다른 기기에서 설정이 변경됐어요. 다시 불러와주세요.',
      422: '입력한 내용을 다시 확인해주세요.',
    };
    const error = new Error(
      messages[response.status]
      || (
        response.status === 400
          ? (safeServerDetail || '입력한 내용을 다시 확인해주세요.')
          : 'AI 맞춤 설정을 처리하지 못했어요. 잠시 후 다시 시도해주세요.'
      )
    );
    error.status = response.status;
    throw error;
  }
  return payload;
};

export const getPersonalization = (currentUser) => (
  request(currentUser, '/api/user/personalization')
);

export const saveTeacherProfile = (currentUser, profile) => (
  request(currentUser, '/api/user/teacher-profile', {
    method: 'PUT',
    body: JSON.stringify(profile),
  })
);

// 🏫 나이스 학교 자동완성. 2자 미만은 서버를 부르지 않고 빈 목록.
// 응답: { success, query, level, sample_mode, schools: [{ school_code, office_code, school_name, school_kind, school_address, ... }] }
export const searchSchools = (currentUser, query, level = '') => {
  const text = String(query || '').trim();
  if (text.length < 2) {
    return Promise.resolve({ success: true, query: text, level, sample_mode: false, schools: [] });
  }
  const params = new URLSearchParams({ q: text.slice(0, 40) });
  if (level) params.set('level', level);
  return request(currentUser, `/api/neis/schools?${params.toString()}`);
};

// 🗓️ 채팅 진입 D-day 제안 (학교가 나이스로 연결된 선생님만 목록이 온다).
// 응답: { success, suggestions: [{ id, title, prompt, category, event, date, dday, dday_label, why }], reason }
export const getDdaySuggestions = (currentUser) => (
  request(currentUser, '/api/neis/suggestions')
);

// 📝 생기부(창체·행동발달) 행사 소재 — 학교 연결 교사만 목록이 온다.
// 응답: { success, topics: [{ id, date, date_dot, dday, dday_label, event, label, kind, area, points[], grades[] }], reason }
export const getSaenggibuEventTopics = (currentUser) => (
  request(currentUser, '/api/neis/saenggibu-topics')
);

export const dismissDdaySuggestions = (currentUser) => (
  request(currentUser, '/api/neis/suggestions/dismiss', { method: 'POST' })
);

export const clearTeacherProfile = (currentUser) => (
  request(currentUser, '/api/user/teacher-profile', {
    method: 'DELETE',
  })
);

export const setMemoryEnabled = (currentUser, enabled) => (
  request(currentUser, '/api/user/memory-settings', {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  })
);

// 채팅 빈 화면의 "30초면 AI가 선생님을 기억해요" 카드를 "나중에"로 닫음.
// 서버(settings.survey_nudge_dismissed_at)에 기록해 기기·워커와 무관하게 다시 띄우지 않는다.
// 재노출 정책: 없음 — 한 번 거절하면 끝 (토스 Respect the No). 설문 진입은 /chat/settings 에서 언제든 가능.
export const dismissSurveyNudge = (currentUser, dismissedAt = new Date().toISOString()) => (
  request(currentUser, '/api/user/memory-settings', {
    method: 'PATCH',
    body: JSON.stringify({ survey_nudge_dismissed_at: dismissedAt }),
  })
);

// 내 자료함 자동 모으기·지난 대화 찾아보기 동의.
// 응답: { feature_enabled, policy_version, should_prompt, consent: { library_auto_collect, past_chat_search, last_prompted_at } }
export const getAiReferenceConsent = (currentUser) => (
  request(currentUser, '/api/user/ai-reference-consent')
);

// changes: { library_auto_collect?: boolean, past_chat_search?: boolean }, source: 'chat_card' | 'settings'
export const updateAiReferenceConsent = (currentUser, changes, source) => (
  request(currentUser, '/api/user/ai-reference-consent', {
    method: 'PUT',
    body: JSON.stringify({ ...changes, source }),
  })
);

// '참고 안 함': 이 대화를 지난 대화 찾아보기에서 뺀다 (excluded=false 면 되돌린다).
export const setPastChatExcluded = (currentUser, conversationId, excluded) => (
  request(currentUser, `/api/user/past-chats/${encodeURIComponent(conversationId)}/excluded`, {
    method: 'PUT',
    body: JSON.stringify({ excluded: Boolean(excluded) }),
  })
);

export const addMemory = (currentUser, content) => (
  request(currentUser, '/api/user/memories', {
    method: 'POST',
    body: JSON.stringify({ content }),
  })
);

export const updateMemory = (currentUser, memoryId, content) => (
  request(
    currentUser,
    `/api/user/memories/${encodeURIComponent(memoryId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ content }),
    }
  )
);

export const deleteMemory = (currentUser, memoryId) => (
  request(
    currentUser,
    `/api/user/memories/${encodeURIComponent(memoryId)}`,
    { method: 'DELETE' }
  )
);

export const clearMemories = (currentUser) => (
  request(currentUser, '/api/user/memories', { method: 'DELETE' })
);

// 🧠 대화 중 AI가 제안한 기억(memory_proposal 카드)을 교사가 "기억하기"로 확정.
// payload: { content, scope, kind, ttl_days, fingerprint, conversation_id }
// 응답: { success, memory, status? }
export const acceptMemoryProposal = (currentUser, payload = {}) => (
  request(currentUser, '/api/user/memories/proposals/accept', {
    method: 'POST',
    body: JSON.stringify({
      content: payload.content,
      scope: payload.scope,
      kind: payload.kind,
      ttl_days: payload.ttl_days,
      fingerprint: payload.fingerprint,
      conversation_id: payload.conversation_id,
    }),
  })
);

// "이번만" — 같은 fingerprint 의 제안을 다시 띄우지 않도록 서버에 기록. 응답: { success }
export const dismissMemoryProposal = (currentUser, fingerprint) => (
  request(currentUser, '/api/user/memories/proposals/dismiss', {
    method: 'POST',
    body: JSON.stringify({ fingerprint }),
  })
);

// 🃏 첫 턴 되묻기 카드 반응 기록 — 2연속 스킵/만료 시 서버가 A모드 카드를 7일 억제한다.
//   result: answered | edited | skipped | timer_expired, mode: A | B. 응답: { success, skip_streak, ... }
//   answers: [{ key, value }] — 실제로 고른 값. 서버는 학교급·학년·과목처럼 안정적인 키만 학습한다
//   (같은 값 2회 → 다음 카드의 추천값이 '지난번', 3회 연속 → 기본값으로 저장되어 더는 묻지 않음).
export const sendClarifyFeedback = (currentUser, { result, mode, answers = [] }) => (
  request(currentUser, '/api/user/chat-clarify/feedback', {
    method: 'POST',
    body: JSON.stringify({
      result,
      mode,
      answers: (answers || [])
        .filter((item) => item && /^[a-z0-9_]{1,24}$/.test(String(item.key || '')) && String(item.value || '').trim())
        .slice(0, 3)
        .map((item) => ({ key: String(item.key), value: String(item.value).trim().slice(0, 60) })),
    }),
  })
);

// '○○ 기준이 기본이에요 · 바꾸기' — 학습된 기본값을 지워 다음 요청부터 다시 카드로 묻게 한다.
export const clearClarifyDefault = (currentUser, key) => (
  request(currentUser, '/api/user/chat-clarify/default/clear', {
    method: 'POST',
    body: JSON.stringify({ key }),
  })
);
